/**
 * Всплывашка «Входящий» над списком «Чатов»: кто звонит прямо сейчас — из своей подписки на события
 * телефонии Bitrix (`GET /api/calls/live`, опрос раз в 3 с). Клик — номер в поиск: есть чат —
 * откроется чат, нет — лиды и сделки номера, как при поиске по телефону, и сразу первая карточка.
 * Спека `docs/specs/2026-10-21-call-live-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { PhoneIncoming, X } from "lucide-react";
import { useEffect, useState } from "react";

import { fmtPhone } from "@/components/chat/Contacts";
import { apiFetch } from "@/lib/api";

/** `user` — кто взял трубку (имя учётки Bitrix); пусто — ещё звонит или учётки нет в копии. */
type LiveCall = { id: string; phone: string; line: string; secs: number; answered: boolean; user: string };

const loadLiveCalls = () => apiFetch<LiveCall[]>("/api/calls/live");

/** Сколько звонит: «0:14» — секунды сервера плюс сколько прошло с его ответа (пояс браузера не важен). */
function ringing(secs: number, sinceFetch: number): string {
  const s = Math.max(0, secs + Math.round(sinceFetch / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function IncomingCalls({ onOpen }: { onOpen: (phone: string) => void }) {
  const live = useQuery({ queryKey: ["calls-live"], queryFn: loadLiveCalls, refetchInterval: 3_000 });
  // Скрытые крестиком — до конца звонка; список звонков живёт минуты, держать в памяти вкладки хватает.
  const [hidden, setHidden] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const calls = (live.data ?? []).filter((c) => !hidden.includes(c.id)).slice(0, 3);
  useEffect(() => {
    if (!calls.length) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [calls.length]);
  if (!calls.length) return null;
  return (
    <div className="flex flex-none flex-col gap-1 px-3 pb-1" data-annot="входящий звонок">
      {calls.map((c) => (
        <div
          key={c.id}
          className={`flex items-center gap-2 rounded-[14px] pl-1 pr-1 text-white shadow-[0_1px_3px_rgba(0,0,0,.25)] ${c.answered ? "bg-[#607d8b]" : "bg-[#2e7d32]"}`}
        >
          <button
            type="button"
            onClick={() => onOpen(c.phone)}
            title="Открыть клиента: чат или лиды и сделки номера"
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[12px] px-2 py-1.5 text-left hover:bg-white/10"
          >
            <PhoneIncoming size={20} className={`flex-none ${c.answered ? "" : "animate-pulse"}`} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {c.answered ? "Взяли" : "Входящий"} {fmtPhone(c.phone)}
              </span>
              <span className="block truncate text-[13px] text-white/80">
                {c.user && <span className="font-medium text-white">{c.user} · </span>}
                {[c.line && `линия «${c.line}»`, ringing(c.secs, now - live.dataUpdatedAt)].filter(Boolean).join(" · ")}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setHidden((h) => [...h, c.id])}
            title="Скрыть"
            className="grid size-8 flex-none place-items-center rounded-full hover:bg-white/15"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
